import { assert, getKeysOfObject, } from "./utils";
export var Level;
(function (Level) {
    Level["fatalError"] = "fatalError";
    Level["recoverableError"] = "recoverableError";
    Level["warning"] = "warning";
    Level["info"] = "info";
})(Level || (Level = {}));
const levelFlagMap = {
    fatalError: 0x0,
    recoverableError: 0x1,
    warning: 0x3,
    info: 0x7,
};
export function computeResultingLevel(manufacturingState) {
    const resultingFlag = manufacturingState.reduce((acc, entry) => acc & levelFlagMap[entry.level], levelFlagMap[Level.info]);
    const result = getKeysOfObject(levelFlagMap)
        .find(key => levelFlagMap[key] === resultingFlag);
    assert(result !== undefined, "Expecting valid level");
    return result;
}
function replyStateMapEntryToEntries(outerKey, replyStateMap, replyStateLevelMap) {
    const replyStateEntryTypeMap = {
        undefined: {
            undefinedError: "unknownError",
        },
        userDefined: {
            undefinedError: "unknownError",
        },
        tubeCutting: {
            undefinedError: "unknownError",
            undetectedFeatures: "backendIssueUndetectedFeatures",
        },
        userDefinedBase: {
            undefinedError: "unknownError",
        },
        sheet: {
            undefinedError: "unknownError",
            nestingFailed: "backendIssueNestingFailed",
        },
        sheetBending: {
            undefinedError: "unknownError",
            undetectedFeatures: "backendIssueUndetectedFeatures",
            multipleOuterContours: "backendIssueMultipleOuterContours",
            contourInBend: "unknownError",
            invalidBend: "backendIssueInvalidBend",
        },
        packaging: {
            undefinedError: "unknownError",
            insufficientPackageVolume: "unknownError",
            overweightParts: "unknownError",
        },
        sheetCutting: {
            undefinedError: "unknownError",
            undetectedFeatures: "backendIssueUndetectedFeatures",
            multipleOuterContours: "backendIssueMultipleOuterContours",
            unused0: "unknownError",
        },
        joining: {
            undefinedError: "unknownError",
        },
        transform: {
            undefinedError: "unknownError",
        },
        tube: {
            undefinedError: "unknownError",
        },
    };
    const replyStateMessageMap = {
        undefined: {
            undefinedError: wsi4.util.translate("error_part"),
        },
        userDefined: {
            undefinedError: wsi4.util.translate("error_part"),
        },
        tubeCutting: {
            undefinedError: wsi4.util.translate("error_part"),
            undetectedFeatures: wsi4.util.translate("undetected_features"),
        },
        userDefinedBase: {
            undefinedError: wsi4.util.translate("error_part"),
        },
        sheet: {
            undefinedError: wsi4.util.translate("error_part"),
            nestingFailed: wsi4.util.translate("nesting_failed"),
        },
        sheetBending: {
            undefinedError: wsi4.util.translate("error_part"),
            undetectedFeatures: wsi4.util.translate("undetected_features"),
            multipleOuterContours: wsi4.util.translate("warning_multiple_outer_contours"),
            contourInBend: wsi4.util.translate("warning_innercontour_in_bend"),
            invalidBend: wsi4.util.translate("error_invalid_bends"),
        },
        packaging: {
            undefinedError: wsi4.util.translate("error_part"),
            insufficientPackageVolume: wsi4.util.translate("error_package_size_too_small"),
            overweightParts: wsi4.util.translate("error_assembly_too_heavy"),
        },
        sheetCutting: {
            undefinedError: wsi4.util.translate("error_part"),
            undetectedFeatures: wsi4.util.translate("undetected_features"),
            multipleOuterContours: wsi4.util.translate("warning_multiple_outer_contours"),
            unused0: "",
        },
        joining: {
            undefinedError: wsi4.util.translate("error_part"),
        },
        transform: {
            undefinedError: wsi4.util.translate("error_part"),
        },
        tube: {
            undefinedError: wsi4.util.translate("error_part"),
        },
    };
    const outerMapEntry = replyStateMap[outerKey];
    if (outerMapEntry === undefined) {
        return [];
    }
    else {
        const indicators = outerMapEntry.replyStateIndicators;
        const innerKeys = getKeysOfObject(indicators);
        return innerKeys.map(innerKey => {
            const typeKey = innerKey;
            const type = replyStateEntryTypeMap[outerKey][typeKey];
            const levelKey = innerKey;
            const level = replyStateLevelMap[outerKey][levelKey];
            const messageKey = innerKey;
            const message = replyStateMessageMap[outerKey][messageKey];
            return {
                type: type,
                level: level,
                message: message,
            };
        });
    }
}
function replyStateMapToEntries(replyStateMap, targetWorkStepTypes, replyStateLevelMap) {
    return getKeysOfObject(replyStateMap)
        .filter(replyState => targetWorkStepTypes.some(wst => wst === replyState))
        .reduce((acc, replyState) => {
        acc.push(...replyStateMapEntryToEntries(replyState, replyStateMap, replyStateLevelMap));
        return acc;
    }, []);
}
function constraintsToEntries(wstConstraintMap, targetWorkStepTypes, constraintLevelMap) {
    const constraintTypeMap = {
        bendAreasNotOverlapping: "constraintBendAreasOverlap",
        bendDie: "constraintNoBendDieAssigned",
        bendFlangeTooShort: "constraintBendFlangeTooShort",
        bendThickness: "constraintNoBendDeductionFound",
        contourInBend: "constraintContourInBend",
        dataCompleteness: "unknownError",
        dataConsistent: "unknownError",
        dataValidity: "unknownError",
        maxBendLineNetLength: "constraintMaxBendLineNetLengthExceeded",
        maxDimensions: "constraintMaxDimensionExceeded",
        maxSheetThickness: "constraintMaxSheetThicknessExceeded",
        minContourSize: "constraintMinContourSizeUndercut",
        sheetAvailability: "constraintSheetNotAvailable",
        transportSource: "unknownError",
        userDefinedProcessId: "unknownError",
        sheetMaterialAvailability: "unknownError",
        profileSupport: "constraintProfileNotSupported",
        tubeAvailability: "constraintTubeNotAvailable",
        tubeCuttingProcessCompatibility: "constraintTubeCuttingProcessCompatibility",
        tubeDetectionLicensed: "constraintTubeDetectionNotLicensed",
        tubeNestingAvailability: "constraintTubeNestingNotAvailable",
    };
    const constraintMessageMap = {
        maxDimensions: wsi4.util.translate("max_dimensions_constraint_violated"),
        sheetAvailability: wsi4.util.translate("sheet_availability_constraint_violated"),
        minContourSize: wsi4.util.translate("min_contour_size_constraint_violated"),
        dataConsistent: wsi4.util.translate("data_consistent_constraint_violated"),
        dataCompleteness: wsi4.util.translate("data_completeness_constraint_violated"),
        dataValidity: wsi4.util.translate("data_validity_constraint_violated"),
        bendDie: wsi4.util.translate("benddie_constraint_violated"),
        bendThickness: wsi4.util.translate("bendthickness_constraint_violated"),
        transportSource: wsi4.util.translate("transport_source_constraint_violated"),
        userDefinedProcessId: wsi4.util.translate("user_defined_process_id_constraint_violated"),
        maxBendLineNetLength: wsi4.util.translate("max_bend_line_net_length_constraint_violated"),
        maxSheetThickness: wsi4.util.translate("max_sheet_thickness_constraint_violated"),
        bendAreasNotOverlapping: wsi4.util.translate("bend_area_not_overlapping_constraint_violated"),
        sheetMaterialAvailability: wsi4.util.translate("sheet_material_not_available"),
        profileSupport: wsi4.util.translate("profile_not_supported"),
        tubeAvailability: wsi4.util.translate("tube_not_available"),
        tubeNestingAvailability: wsi4.util.translate("nesting_failed"),
        tubeCuttingProcessCompatibility: wsi4.util.translate("process_not_applicable"),
        tubeDetectionLicensed: wsi4.util.translate("tube_detection_not_licensed"),
        contourInBend: wsi4.util.translate("warning_innercontour_in_bend"),
        bendFlangeTooShort: wsi4.util.translate("bend_flange_too_short"),
    };
    return getKeysOfObject(wstConstraintMap)
        .filter(lhs => targetWorkStepTypes.some(rhs => lhs === rhs))
        .reduce((acc, wst) => {
        const constraints = wstConstraintMap[wst];
        const entries = (() => {
            if (constraints === undefined) {
                return [];
            }
            else {
                return getKeysOfObject(constraints)
                    .filter(key => constraints[key])
                    .map(key => ({
                    type: constraintTypeMap[key],
                    level: constraintLevelMap[key],
                    message: constraintMessageMap[key],
                }));
            }
        })();
        acc.push(...entries);
        return acc;
    }, []);
}
export function computeManufacturingStateImpl(replyStateIndicatorMap, constraintMap, replyStateLevelMap, constraintLevelMap, targetWorkStepTypes) {
    return [
        ...replyStateMapToEntries(replyStateIndicatorMap, targetWorkStepTypes, replyStateLevelMap),
        ...constraintsToEntries(constraintMap, targetWorkStepTypes, constraintLevelMap),
    ];
}
export function isExportReadyImpl(manufacturingState) {
    switch (computeResultingLevel(manufacturingState)) {
        case Level.fatalError: return false;
        case Level.recoverableError: return false;
        case Level.warning: return true;
        case Level.info: return true;
    }
}
